'use client';

import { Trash2 } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/cn';

export interface NotificationCardProps {
	/** Titolo della notifica. */
	title: React.ReactNode;
	/** Piccolo tag di categoria bordato accanto al titolo. */
	typeLabel?: React.ReactNode;
	/** Testo della notifica, troncato a 2 righe. */
	message?: React.ReactNode | null;
	/** Data/ora formattata. */
	dateLabel: React.ReactNode;
	/** Se true, mostra il pallino e il badge "Nuova". */
	unread?: boolean;
	/** Se true, indica che la notifica è stata inviata dall'utente (accento viola) invece che ricevuta. */
	sent?: boolean;
	/** Chiamata al click sulla riga. */
	onClick?: () => void;
	/** Se presente, mostra il cestino a destra e chiama questa funzione al click. */
	onDelete?: () => void;
}

/** Riga di notifica cliccabile: pallino non letta, titolo, tag di categoria, messaggio troncato, data. */
const NotificationCard = ({
	title,
	typeLabel,
	message,
	dateLabel,
	unread = false,
	sent = false,
	onClick,
	onDelete,
}: NotificationCardProps) => (
	<div
		className={cn(
			'flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-colors',
			sent
				? 'border-accent-purple/30 bg-accent-purple/5 hover:border-accent-purple/50'
				: unread
					? 'border-accent/40 bg-accent/[0.10] hover:border-accent/60'
					: 'border-border bg-card hover:border-accent/30',
		)}
	>
		<div
			className={cn(
				'mt-1 h-2 w-2 shrink-0 rounded-full',
				sent ? 'bg-accent-purple' : unread ? 'bg-accent' : 'bg-transparent',
			)}
		/>
		<button type="button" onClick={onClick} className="min-w-0 flex-1 text-left">
			<div className="flex flex-wrap items-center gap-2">
				<span className="text-[13px] font-semibold text-text-primary">{title}</span>
				{typeLabel && (
					<span className="rounded border border-border px-1.5 py-0.5 text-[11px] text-text-muted">
						{typeLabel}
					</span>
				)}
				{sent ? (
					<span className="rounded-full bg-accent-purple/10 px-1.5 py-0.5 text-[11px] font-semibold text-accent-purple">
						Inviata
					</span>
				) : (
					unread && (
						<span className="rounded-full bg-accent/10 px-1.5 py-0.5 text-[11px] font-semibold text-accent">
							Nuova
						</span>
					)
				)}
			</div>
			{message && <p className="mt-1 line-clamp-2 text-[13px] text-text-muted">{message}</p>}
			<p className="mt-1 text-[11px] text-text-muted">{dateLabel}</p>
		</button>
		{/*
		 * Senza `onDelete` qui non va nulla: c'era un'icona "i" decorativa che
		 * sembrava un pulsante e al click non faceva niente (INB-111). Il
		 * dettaglio si apre dalla riga, che è già interamente cliccabile.
		 */}
		{onDelete && (
			<button
				type="button"
				onClick={onDelete}
				aria-label="Elimina notifica"
				title="Elimina notifica"
				className="mt-0.5 shrink-0 rounded p-1 text-text-muted transition-colors hover:bg-danger/10 hover:text-danger"
			>
				<Trash2 className="h-3.5 w-3.5" />
			</button>
		)}
	</div>
);
NotificationCard.displayName = 'NotificationCard';

export { NotificationCard };
